import { useTranslation } from "react-i18next";
import type { PersonRequestHistoryItem } from "../../api/requestsAdmin";
import teamsIcon from "./teams.svg";
import { shortEmail } from "./requestHistoryFormat";
import styles from "./WaitingWith.module.css";

/** The deep link that opens a Microsoft Teams chat with a person, given their work email address. */
function teamsChatUrl(email: string): string {
  return `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`;
}

/**
 * Who a waiting request is with, so its requester can follow up: the assignee's name and a link that opens a Teams chat with them.
 * It shows nothing for a request that is decided, or one that is not with a particular person.
 *
 * @param props.item - The request.
 */
export function WaitingWith({
  item,
}: {
  item: PersonRequestHistoryItem;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const email = item.assignedToEmail ?? "";
  if (email === "") {
    return null;
  }

  const trimmed = item.assignedToName?.trim() ?? "";
  const name = trimmed === "" ? (shortEmail(email) ?? email) : trimmed;
  return (
    <p className={styles.waiting}>
      <span>{t("myRequests.waitingWith", { name })}</span>
      <a
        className={styles.chat}
        href={teamsChatUrl(email)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${t("myRequests.chatOnTeams", { name })} ${t("myRequests.opensInNewTab")}`}
      >
        <img src={teamsIcon} alt="" width="20" height="20" />
        <span className={`chart-tooltip ${styles.tip}`} aria-hidden="true">
          {t("myRequests.chatOnTeams", { name })}
        </span>
      </a>
    </p>
  );
}
